import React, { useState, useEffect, useCallback } from 'react';
import { C } from '../../shared/theme';
import { notifyUser } from '../../shared/dbService';

const BASE = process.env.REACT_APP_API_URL || '/api';

// ── Office palette ─────────────────────────────────────────────────────────────
const O = {
  bg:       '#08111f',
  bg2:      '#0d1a2e',
  bg3:      '#111e30',
  bg4:      '#162540',
  border:   '#1e3352',
  gold:     '#C8A84B',
  goldDim:  '#8a7230',
  teal:     '#2A9E82',
  blue:     '#5B9ED0',
  slate:    '#6b7a99',
  slate2:   '#4a5878',
  red:      '#E05C5C',
  green:    '#4AC87A',
};

const LICENSES = ['freemium','individual','starter','athlete','sensei','professional','companies','coaches_solo','coaches_team','coaches_pro'];

const LIC_COLOR = {
  freemium: O.slate2, individual: O.teal, starter: '#7B9ED0',
  athlete: O.blue, sensei: '#9B6DB0', professional: O.gold,
  companies: O.green, coaches_solo: '#C06090', coaches_team: '#D4873A', coaches_pro: O.gold,
};

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function daysAgo(d) {
  if (!d) return '—';
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (diff === 0) return 'today';
  if (diff === 1) return '1d ago';
  return `${diff}d ago`;
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon, color = O.gold, sub }) {
  return (
    <div style={{
      background: `linear-gradient(140deg, ${O.bg3}, ${O.bg2})`,
      border: `1px solid ${O.border}`,
      borderTop: `2px solid ${color}`,
      borderRadius: 16, padding: '18px 16px', minWidth: 0,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ fontSize: 10, color: O.slate, letterSpacing: 2, fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 20, opacity: 0.7 }}>{icon}</div>
      </div>
      <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1, letterSpacing: -1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 10, color: O.slate2, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ── Horizontal bar ────────────────────────────────────────────────────────────
function LicBar({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 12, color: color || O.slate, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 12, color: C.white, fontWeight: 700 }}>{count} <span style={{ color: O.slate, fontWeight: 400 }}>({pct}%)</span></span>
      </div>
      <div style={{ height: 5, background: O.bg4, borderRadius: 3 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color || O.gold, borderRadius: 3, transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
}

// ── Live pulse dot ────────────────────────────────────────────────────────────
function PulseDot({ color = O.green }) {
  return (
    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}`, marginRight: 6, verticalAlign: 'middle', animation: 'pulse 2s infinite' }} />
  );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHead({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, marginTop: 6 }}>
      <div style={{ flex: 1, height: 1, background: O.border }} />
      <div style={{ fontSize: 10, color: O.slate, letterSpacing: 2.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{children}</div>
      <div style={{ flex: 1, height: 1, background: O.border }} />
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminDashboardScreen({ lang, currentUserId, onBack }) {
  const nl = lang === 'nl';
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3200); };

  const loadMetrics = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/admin/metrics?uid=${currentUserId}`);
      if (!r.ok) throw new Error(await r.text());
      setMetrics(await r.json());
    } catch (e) { setError(e.message); }
  }, [currentUserId]);

  const loadUsers = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/admin/users?uid=${currentUserId}&page=${p}&q=${encodeURIComponent(q)}`);
      if (!r.ok) throw new Error(await r.text());
      const d = await r.json();
      setUsers(d.users || []); setTotal(d.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [currentUserId]);

  useEffect(() => {
    if (activeTab === 'overview') loadMetrics();
    if (activeTab === 'members') loadUsers(page, query);
    // activity tab loaded lazily in its own effect
  }, [activeTab]); // eslint-disable-line

  const handleSearch = (q) => { setQuery(q); setPage(1); loadUsers(1, q); };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      license: u.license || 'freemium',
      license_expires_at: u.license_expires_at ? u.license_expires_at.slice(0, 10) : '',
      is_admin: u.is_admin ? '1' : '0',
      is_researcher: u.is_researcher ? '1' : '0',
      wallet_credit: '0',
    });
  };

  const saveEdit = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const body = {
        uid: currentUserId,
        license: editForm.license,
        is_admin: parseInt(editForm.is_admin),
        is_researcher: parseInt(editForm.is_researcher),
        wallet_credit: parseInt(editForm.wallet_credit || '0'),
      };
      if (editForm.license_expires_at) body.license_expires_at = editForm.license_expires_at;
      if (editForm.license !== 'freemium') body.license_granted_by = currentUserId;
      const r = await fetch(`${BASE}/admin/users/${editUser.id}?uid=${currentUserId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      // Notify target user of rights/credits changes
      const adminGranted = parseInt(editForm.is_admin) === 1 && !editUser.is_admin;
      const labGranted   = parseInt(editForm.is_researcher) === 1 && !editUser.is_researcher;
      const creditsAdded = parseInt(editForm.wallet_credit || '0') > 0;
      if (adminGranted) notifyUser(editUser.id, 'admin_rights', 'Admin', 'You have been granted admin access.');
      if (labGranted)   notifyUser(editUser.id, 'lab_rights',   'Admin', 'You have been granted lab (researcher) access.');
      if (creditsAdded) notifyUser(editUser.id, 'credits_received', 'Admin', `You received ${editForm.wallet_credit} credits from admin.`);
      showToast('Saved successfully');
      setEditUser(null); loadUsers(page, query);
    } catch (e) { showToast(e.message, false); }
    finally { setSaving(false); }
  };

  const blockUser = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try {
      await fetch(`${BASE}/admin/users/${u.id}?uid=${currentUserId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: currentUserId, block: true }),
      });
      showToast('User removed.'); loadUsers(page, query);
    } catch (e) { showToast(e.message, false); }
  };

  const TABS = [
    { id: 'overview',  icon: '◈', label: 'Overview'  },
    { id: 'members',   icon: '◈', label: 'Members'   },
    { id: 'activity',  icon: '◈', label: 'Activity'  },
    { id: 'revenue',   icon: '◈', label: 'Revenue'   },
    { id: 'tools',     icon: '◈', label: 'Tools'     },
  ];

  // ── Tools tab state ───────────────────────────────────────────────────────
  const [smsPhone,   setSmsPhone]   = useState('');
  const [smsTesting, setSmsTesting] = useState(false);
  const [smsResult,  setSmsResult]  = useState(null);

  const [activity, setActivity]       = useState(null);
  const [actLoading, setActLoading]   = useState(false);
  const [actSearch, setActSearch]     = useState('');

  const loadActivity = useCallback(async () => {
    setActLoading(true);
    try {
      const r = await fetch(`${BASE}/admin/activity?uid=${currentUserId}`);
      if (!r.ok) throw new Error(await r.text());
      setActivity(await r.json());
    } catch (e) { setError(e.message); }
    finally { setActLoading(false); }
  }, [currentUserId]);

  useEffect(() => {
    if (activeTab === 'activity' && !activity) loadActivity();
  }, [activeTab]); // eslint-disable-line

  const totalLic = metrics ? Object.values(metrics.by_license || {}).reduce((a, b) => a + b, 0) : 0;
  const paidCount = metrics ? Object.entries(metrics.by_license || {}).filter(([k]) => k !== 'freemium').reduce((a, [, v]) => a + v, 0) : 0;
  const estMrr = metrics ? Object.entries(metrics.by_license || {}).reduce((acc, [lic, cnt]) => {
    const prices = { starter: 9, athlete: 19, sensei: 39, professional: 79, individual: 19, companies: 149, coaches_solo: 29, coaches_team: 59, coaches_pro: 99 };
    return acc + (prices[lic] || 0) * cnt;
  }, 0) : 0;

  return (
    <div style={{ background: O.bg, minHeight: '100vh', fontFamily: 'Inter,system-ui,sans-serif', color: C.white }}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1;} 50%{opacity:0.4;} }
        @keyframes slideUp { from{opacity:0;transform:translateY(12px);} to{opacity:1;transform:none;} }
        .office-tab-content { animation: slideUp 0.25s ease; }
      `}</style>

      {/* ── Executive header ──────────────────────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, ${O.bg2} 0%, #0f1e35 100%)`,
        borderBottom: `1px solid ${O.border}`,
        padding: '20px 20px 0', position: 'sticky', top: 0, zIndex: 50, backdropFilter: 'blur(20px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: O.slate, fontSize: 20, cursor: 'pointer', padding: 0, lineHeight: 1 }}>←</button>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: C.white, letterSpacing: -0.5, lineHeight: 1 }}>
              Sense Lifting <span style={{ color: O.gold }}>Office</span>
            </div>
            <div style={{ fontSize: 10, color: O.slate, letterSpacing: 3, marginTop: 3 }}>
              <PulseDot />LIVE · PLATFORM MANAGEMENT
            </div>
          </div>
          <div style={{ marginLeft: 'auto', background: `${O.gold}15`, border: `1px solid ${O.gold}40`, borderRadius: 10, padding: '6px 12px' }}>
            <div style={{ fontSize: 9, color: O.goldDim, letterSpacing: 2 }}>ADMINISTRATOR</div>
          </div>
        </div>

        {/* Tab nav */}
        <div style={{ display: 'flex', gap: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
              flex: 1, background: 'none', border: 'none', padding: '10px 4px 12px', cursor: 'pointer',
              fontSize: 12, fontWeight: 700, letterSpacing: 1,
              color: activeTab === t.id ? O.gold : O.slate,
              borderBottom: `2px solid ${activeTab === t.id ? O.gold : 'transparent'}`,
              transition: 'all 0.2s',
            }}>
              {t.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '20px 16px 100px' }} className="office-tab-content">
        {error && (
          <div style={{ background: `${O.red}15`, border: `1px solid ${O.red}44`, borderRadius: 12, padding: '12px 16px', color: O.red, marginBottom: 16, fontSize: 13 }}>
            ⚠ {error}
          </div>
        )}

        {/* ── OVERVIEW ──────────────────────────────────────────────────── */}
        {activeTab === 'overview' && metrics && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
              <KpiCard label="TOTAL MEMBERS"   value={metrics.total_users?.toLocaleString()}  icon="👥" color={O.blue}  sub="all registered" />
              <KpiCard label="ONBOARDED"        value={metrics.onboarded?.toLocaleString()}    icon="✅" color={O.green} sub={`${Math.round((metrics.onboarded/Math.max(metrics.total_users,1))*100)}% conversion`} />
              <KpiCard label="ACTIVE 24H"       value={metrics.active_24h?.toLocaleString()}   icon="⚡" color={O.gold} />
              <KpiCard label="ACTIVE 7 DAYS"    value={metrics.active_7d?.toLocaleString()}    icon="📈" color={O.teal} />
              <KpiCard label="NEW TODAY"        value={metrics.signups_24h?.toLocaleString()}  icon="🆕" color={O.blue} />
              <KpiCard label="NEW THIS WEEK"    value={metrics.signups_7d?.toLocaleString()}   icon="📅" color={O.blue} />
              <KpiCard label="SESSIONS 24H"     value={metrics.exercises_24h?.toLocaleString()} icon="🧘" color={O.teal} />
              <KpiCard label="CHECK-INS 24H"    value={metrics.checkins_24h?.toLocaleString()} icon="😴" color={O.teal} />
            </div>

            <SectionHead>LICENSE DISTRIBUTION</SectionHead>
            <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 16, padding: '20px 18px', marginBottom: 24 }}>
              {Object.entries(metrics.by_license || {}).sort(([,a],[,b]) => b-a).map(([lic, cnt]) => (
                <LicBar key={lic} label={lic} count={cnt} total={totalLic} color={LIC_COLOR[lic]} />
              ))}
            </div>

            <SectionHead>AUTH METHODS</SectionHead>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 24 }}>
              {Object.entries(metrics.by_auth || {}).map(([method, cnt]) => (
                <div key={method} style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 14, padding: '16px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, marginBottom: 6 }}>
                    {method === 'google' ? '🔵' : method === 'email' ? '📧' : '👤'}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.white }}>{cnt}</div>
                  <div style={{ fontSize: 10, color: O.slate, marginTop: 3, letterSpacing: 1 }}>{method.toUpperCase()}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
              <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 14, padding: '16px' }}>
                <div style={{ fontSize: 10, color: O.slate, letterSpacing: 2, marginBottom: 6 }}>COMPANIES</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#7B6FD0' }}>{metrics.businesses_total ?? '—'}</div>
                <div style={{ fontSize: 10, color: O.slate2, marginTop: 4 }}>{metrics.business_members ?? 0} members</div>
              </div>
              <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 14, padding: '16px' }}>
                <div style={{ fontSize: 10, color: O.slate, letterSpacing: 2, marginBottom: 6 }}>CREDITS SPENT 7D</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: O.gold }}>{metrics.credits_spent_7d ?? '—'}</div>
                <div style={{ fontSize: 10, color: O.slate2, marginTop: 4 }}>wallet transactions</div>
              </div>
            </div>

            <button onClick={loadMetrics} style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 12, color: O.gold, padding: '10px 20px', fontSize: 12, cursor: 'pointer', fontWeight: 700, letterSpacing: 1 }}>
              ↻ REFRESH DATA
            </button>
          </div>
        )}

        {/* ── MEMBERS ───────────────────────────────────────────────────── */}
        {activeTab === 'members' && (
          <div>
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <input
                value={query} onChange={e => handleSearch(e.target.value)}
                placeholder="Search members by name or email…"
                style={{ width: '100%', background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 12, color: C.white, fontSize: 14, padding: '12px 16px 12px 42px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
              />
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: O.slate, fontSize: 16 }}>⌕</span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 11, color: O.slate, letterSpacing: 1 }}>{total.toLocaleString()} MEMBERS{query && ` · "${query}"`}</span>
              {loading && <span style={{ fontSize: 11, color: O.gold }}>Loading…</span>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {users.map(u => (
                <div key={u.id} style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 14, padding: '14px 16px', transition: 'border-color 0.2s' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    {u.picture
                      ? <img src={u.picture} alt="" style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${LIC_COLOR[u.license] || O.border}` }} />
                      : <div style={{ width: 42, height: 42, borderRadius: '50%', background: O.bg4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0, border: `2px solid ${O.border}` }}>👤</div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {u.name || '(no name)'}
                        {u.is_admin && <span style={{ fontSize: 9, background: `${O.gold}20`, color: O.gold, borderRadius: 4, padding: '1px 5px', fontWeight: 700, letterSpacing: 1 }}>OFFICE</span>}
                        {u.is_researcher && <span style={{ fontSize: 9, background: `${O.blue}20`, color: O.blue, borderRadius: 4, padding: '1px 5px', fontWeight: 700, letterSpacing: 1 }}>LAB</span>}
                      </div>
                      <div style={{ fontSize: 11, color: O.slate, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{u.email || '—'}</div>
                    </div>
                    <div style={{ background: `${LIC_COLOR[u.license] || O.slate2}18`, border: `1px solid ${LIC_COLOR[u.license] || O.border}44`, borderRadius: 8, padding: '4px 10px', flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: LIC_COLOR[u.license] || O.slate }}>{u.license || 'freemium'}</span>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, marginBottom: 12 }}>
                    {[
                      { label: 'Sessions', val: u.ex_count, color: O.teal },
                      { label: 'Sleep', val: u.sleep_count, color: O.blue },
                      { label: 'Credits', val: u.wallet_balance, color: O.gold },
                      { label: 'Joined', val: fmt(u.created_at), color: O.slate },
                    ].map(({ label, val, color }) => (
                      <div key={label} style={{ background: O.bg4, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color }}>{val}</div>
                        <div style={{ fontSize: 9, color: O.slate2, marginTop: 2, letterSpacing: 0.5 }}>{label}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 10, color: O.slate2 }}>Last active: {daysAgo(u.last_seen)}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openEdit(u)} style={{ background: `${O.gold}15`, border: `1px solid ${O.gold}44`, borderRadius: 8, color: O.gold, fontSize: 11, fontWeight: 700, padding: '6px 12px', cursor: 'pointer', letterSpacing: 0.5 }}>
                        EDIT
                      </button>
                      <button onClick={() => blockUser(u)} style={{ background: `${O.red}10`, border: `1px solid ${O.red}33`, borderRadius: 8, color: O.red, fontSize: 11, fontWeight: 700, padding: '6px 12px', cursor: 'pointer', letterSpacing: 0.5 }}>
                        REMOVE
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {total > 50 && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20, alignItems: 'center' }}>
                <button disabled={page <= 1} onClick={() => { const p = page-1; setPage(p); loadUsers(p,query); }}
                  style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 10, color: page > 1 ? C.white : O.slate2, padding: '8px 16px', cursor: page > 1 ? 'pointer' : 'default', fontSize: 12 }}>
                  ← Prev
                </button>
                <span style={{ fontSize: 12, color: O.gold, background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 10, padding: '8px 14px' }}>
                  {page} / {Math.ceil(total / 50)}
                </span>
                <button disabled={page >= Math.ceil(total/50)} onClick={() => { const p = page+1; setPage(p); loadUsers(p,query); }}
                  style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 10, color: page < Math.ceil(total/50) ? C.white : O.slate2, padding: '8px 16px', cursor: page < Math.ceil(total/50) ? 'pointer' : 'default', fontSize: 12 }}>
                  Next →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── ACTIVITY ──────────────────────────────────────────────────── */}
        {activeTab === 'activity' && (
          <div>
            {actLoading && <div style={{ textAlign: 'center', padding: 40, color: O.slate }}>Loading…</div>}
            {activity && (
              <>
                {/* Platform-wide top */}
                <SectionHead>TOP GAMES · LAST 30 DAYS</SectionHead>
                <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: 20 }}>
                  {activity.top_games?.map((g, i) => (
                    <div key={g.game_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: `1px solid ${O.border}22` }}>
                      <span style={{ fontSize: 11, color: O.slate, width: 18 }}>#{i+1}</span>
                      <span style={{ flex: 1, fontSize: 12, color: C.white, fontWeight: 600 }}>{g.game_id}</span>
                      <span style={{ fontSize: 11, color: O.teal }}>{g.plays} plays</span>
                      <span style={{ fontSize: 10, color: O.slate }}>{g.players} users</span>
                    </div>
                  ))}
                </div>

                <SectionHead>TOP EXERCISES · LAST 30 DAYS</SectionHead>
                <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: 20 }}>
                  {activity.top_exercises?.map((e, i) => (
                    <div key={e.ex_id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: `1px solid ${O.border}22` }}>
                      <span style={{ fontSize: 11, color: O.slate, width: 18 }}>#{i+1}</span>
                      <span style={{ flex: 1, fontSize: 12, color: C.white, fontWeight: 600 }}>{e.ex_id}</span>
                      <span style={{ fontSize: 11, color: O.blue }}>{e.sessions} sessions</span>
                      <span style={{ fontSize: 10, color: O.slate }}>{e.users} users</span>
                    </div>
                  ))}
                </div>

                {/* Per-user breakdown */}
                <SectionHead>USER ACTIVITY BREAKDOWN</SectionHead>
                <div style={{ position: 'relative', marginBottom: 12 }}>
                  <input value={actSearch} onChange={e => setActSearch(e.target.value)}
                    placeholder="Filter by name or email…"
                    style={{ width: '100%', background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 12, color: C.white, fontSize: 13, padding: '10px 16px 10px 40px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: O.slate }}>⌕</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(activity.users || [])
                    .filter(u => !actSearch || u.name?.toLowerCase().includes(actSearch.toLowerCase()) || u.email?.toLowerCase().includes(actSearch.toLowerCase()))
                    .map(u => (
                    <div key={u.id} style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 14, padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: O.bg4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, border: `2px solid ${LIC_COLOR[u.license] || O.border}` }}>👤</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || '(no name)'}</div>
                          <div style={{ fontSize: 10, color: O.slate }}>{u.email}</div>
                        </div>
                        <div style={{ fontSize: 10, color: LIC_COLOR[u.license] || O.slate, background: `${LIC_COLOR[u.license] || O.slate}18`, border: `1px solid ${LIC_COLOR[u.license] || O.border}44`, borderRadius: 6, padding: '3px 8px' }}>{u.license}</div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: u.games?.length || u.exercises?.length ? 10 : 0 }}>
                        <div style={{ background: O.bg4, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: O.teal }}>{u.game_plays}</div>
                          <div style={{ fontSize: 9, color: O.slate2, marginTop: 2 }}>GAME PLAYS</div>
                        </div>
                        <div style={{ background: O.bg4, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: O.blue }}>{u.ex_sessions}</div>
                          <div style={{ fontSize: 9, color: O.slate2, marginTop: 2 }}>EXERCISES</div>
                        </div>
                        <div style={{ background: O.bg4, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: O.gold }}>{u.credits?.spent ?? 0}</div>
                          <div style={{ fontSize: 9, color: O.slate2, marginTop: 2 }}>CREDITS SPENT</div>
                        </div>
                      </div>
                      {u.games?.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ fontSize: 9, color: O.slate, letterSpacing: 2, marginBottom: 4 }}>GAMES</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {u.games.map(g => (
                              <span key={g.game} style={{ fontSize: 10, background: `${O.teal}15`, color: O.teal, border: `1px solid ${O.teal}33`, borderRadius: 6, padding: '2px 8px' }}>
                                {g.game} ×{g.plays}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {u.exercises?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 9, color: O.slate, letterSpacing: 2, marginBottom: 4 }}>EXERCISES</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {u.exercises.slice(0, 5).map(e => (
                              <span key={e.ex} style={{ fontSize: 10, background: `${O.blue}15`, color: O.blue, border: `1px solid ${O.blue}33`, borderRadius: 6, padding: '2px 8px' }}>
                                {e.ex} ×{e.sessions}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <button onClick={loadActivity} style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 12, color: O.gold, padding: '10px 20px', fontSize: 12, cursor: 'pointer', fontWeight: 700, letterSpacing: 1 }}>↻ REFRESH</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── REVENUE ───────────────────────────────────────────────────── */}
        {activeTab === 'revenue' && metrics && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 24 }}>
              <KpiCard label="PAID MEMBERS"  value={paidCount.toLocaleString()}     icon="💳" color={O.gold}  sub="active licenses" />
              <KpiCard label="EST. MRR"      value={`€${estMrr.toLocaleString()}`}  icon="📈" color={O.green} sub="monthly recurring" />
              <KpiCard label="FREEMIUM"      value={(metrics.by_license?.freemium || 0).toLocaleString()} icon="🔓" color={O.slate} sub="conversion opportunity" />
              <KpiCard label="CONVERSION"    value={`${totalLic ? Math.round((paidCount/totalLic)*100) : 0}%`} icon="🎯" color={O.teal} sub="free → paid" />
            </div>

            <SectionHead>REVENUE BY TIER</SectionHead>
            <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 16, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 0 }}>
                {['PLAN','MEMBERS','PRICE','MRR'].map(h => (
                  <div key={h} style={{ padding: '10px 14px', fontSize: 9, color: O.slate, letterSpacing: 2, fontWeight: 700, borderBottom: `1px solid ${O.border}`, background: O.bg4 }}>{h}</div>
                ))}
                {Object.entries(metrics.by_license || {}).filter(([k]) => k !== 'freemium').sort(([,a],[,b]) => b-a).map(([lic, cnt]) => {
                  const prices = { starter:9, athlete:19, sensei:39, professional:79, individual:19, companies:149, coaches_solo:29, coaches_team:59, coaches_pro:99 };
                  const price = prices[lic] || 0;
                  const mrr = price * cnt;
                  return (
                    <>
                      <div key={lic+'-n'} style={{ padding: '12px 14px', fontSize: 12, fontWeight: 600, color: LIC_COLOR[lic] || O.gold, borderBottom: `1px solid ${O.border}20` }}>{lic}</div>
                      <div key={lic+'-c'} style={{ padding: '12px 14px', fontSize: 12, color: C.white, fontWeight: 700, textAlign: 'right', borderBottom: `1px solid ${O.border}20` }}>{cnt}</div>
                      <div key={lic+'-p'} style={{ padding: '12px 14px', fontSize: 11, color: O.slate, textAlign: 'right', borderBottom: `1px solid ${O.border}20` }}>€{price}/mo</div>
                      <div key={lic+'-m'} style={{ padding: '12px 14px', fontSize: 12, color: O.green, fontWeight: 700, textAlign: 'right', borderBottom: `1px solid ${O.border}20` }}>€{mrr}</div>
                    </>
                  );
                })}
              </div>
            </div>

            <SectionHead>PLATFORM HEALTH</SectionHead>
            <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 16, padding: '18px' }}>
              {[
                { label: 'DAU / MAU ratio', value: metrics.active_24h && metrics.total_users ? `${Math.round((metrics.active_24h / metrics.total_users) * 100)}%` : '—', color: O.teal },
                { label: 'Weekly retention proxy', value: metrics.active_7d && metrics.total_users ? `${Math.round((metrics.active_7d / metrics.total_users) * 100)}%` : '—', color: O.blue },
                { label: 'Session engagement 24h', value: metrics.exercises_24h?.toLocaleString() || '—', color: O.green },
                { label: 'Credit economy 7d', value: metrics.credits_spent_7d?.toLocaleString() || '—', color: O.gold },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${O.border}33` }}>
                  <span style={{ fontSize: 12, color: O.slate }}>{row.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: row.color }}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── TOOLS ──────────────────────────────────────────────────────── */}
        {activeTab === 'tools' && (
          <div>
            <SectionHead>SMS DIAGNOSTICS</SectionHead>
            <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 16, padding: '20px 18px', marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: O.slate, marginBottom: 16, lineHeight: 1.6 }}>
                Test Twilio SMS delivery. Sends a real SMS to the number you enter and shows the full Twilio API response including any error codes.
              </div>

              {/* Phone input */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: O.slate, letterSpacing: 2, fontWeight: 700, marginBottom: 6 }}>RECIPIENT PHONE NUMBER</div>
                <input
                  value={smsPhone}
                  onChange={e => { setSmsPhone(e.target.value); setSmsResult(null); }}
                  placeholder="+31612345678 or 0612345678"
                  style={{ width: '100%', boxSizing: 'border-box', background: O.bg4, border: `1px solid ${O.border}`, borderRadius: 10, padding: '11px 14px', color: C.white, fontSize: 14, outline: 'none' }}
                />
              </div>

              <button
                onClick={async () => {
                  if (!smsPhone.trim()) return;
                  setSmsTesting(true); setSmsResult(null);
                  try {
                    const r = await fetch(`${BASE}/admin/test-sms?uid=${currentUserId}`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ phone: smsPhone.trim() }),
                    });
                    const d = await r.json();
                    setSmsResult({ ok: r.ok, data: d });
                  } catch (e) {
                    setSmsResult({ ok: false, data: { error: e.message } });
                  }
                  setSmsTesting(false);
                }}
                disabled={smsTesting || !smsPhone.trim()}
                style={{ width: '100%', background: smsTesting ? O.bg4 : `linear-gradient(135deg, ${O.teal}, #1a7a62)`, border: 'none', borderRadius: 12, padding: '13px', color: C.white, fontSize: 14, fontWeight: 700, cursor: smsTesting ? 'default' : 'pointer', opacity: !smsPhone.trim() ? 0.5 : 1, marginBottom: smsResult ? 16 : 0 }}
              >
                {smsTesting ? '⏳ Sending SMS...' : '📱 Send Test SMS'}
              </button>

              {/* Result display */}
              {smsResult && (
                <div style={{ marginTop: 4 }}>
                  {/* Status banner */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10, marginBottom: 12, background: smsResult.data?.success ? 'rgba(74,200,122,0.1)' : 'rgba(224,92,92,0.1)', border: `1px solid ${smsResult.data?.success ? 'rgba(74,200,122,0.3)' : 'rgba(224,92,92,0.3)'}` }}>
                    <span style={{ fontSize: 22 }}>{smsResult.data?.success ? '✅' : '❌'}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: smsResult.data?.success ? O.green : O.red }}>
                        {smsResult.data?.success ? 'SMS delivered to Twilio' : 'SMS failed'}
                      </div>
                      <div style={{ fontSize: 11, color: O.slate, marginTop: 2 }}>
                        HTTP {smsResult.data?.http_code} · Sent to {smsResult.data?.e164 || smsPhone}
                      </div>
                    </div>
                  </div>

                  {/* Quick fields */}
                  {[
                    { label: 'E.164 number',  value: smsResult.data?.e164 },
                    { label: 'HTTP code',     value: smsResult.data?.http_code },
                    { label: 'Message SID',   value: smsResult.data?.twilio_raw?.sid },
                    { label: 'Status',        value: smsResult.data?.twilio_raw?.status },
                    { label: 'Error code',    value: smsResult.data?.twilio_raw?.code },
                    { label: 'Error message', value: smsResult.data?.twilio_raw?.message },
                    { label: 'From number',   value: smsResult.data?.from },
                    { label: 'cURL error',    value: smsResult.data?.curl_error },
                  ].filter(f => f.value != null && f.value !== '').map(f => (
                    <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: `1px solid ${O.border}33`, gap: 12 }}>
                      <span style={{ fontSize: 11, color: O.slate, flexShrink: 0 }}>{f.label}</span>
                      <span style={{ fontSize: 11, color: C.white, fontWeight: 600, textAlign: 'right', wordBreak: 'break-all' }}>{String(f.value)}</span>
                    </div>
                  ))}

                  {/* Raw JSON */}
                  <details style={{ marginTop: 12 }}>
                    <summary style={{ fontSize: 11, color: O.slate, cursor: 'pointer', userSelect: 'none' }}>Raw Twilio response ›</summary>
                    <pre style={{ marginTop: 8, background: O.bg4, borderRadius: 8, padding: '12px', fontSize: 10, color: O.slate, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {JSON.stringify(smsResult.data?.twilio_raw, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>

            <SectionHead>EMAIL DIAGNOSTICS</SectionHead>
            <div style={{ background: O.bg3, border: `1px solid ${O.border}`, borderRadius: 16, padding: '20px 18px', marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: O.slate, lineHeight: 1.7 }}>
                Email is sent via PHP <code style={{ background: O.bg4, padding: '1px 5px', borderRadius: 4 }}>mail()</code> which relies on the server's MTA (sendmail/postfix).<br/>
                If emails are not arriving, check:<br/>
                <ol style={{ paddingLeft: 20, marginTop: 8, color: O.slate }}>
                  <li>Server has sendmail/postfix installed and configured</li>
                  <li><code style={{ background: O.bg4, padding: '1px 5px', borderRadius: 4 }}>mail.log</code> or <code style={{ background: O.bg4, padding: '1px 5px', borderRadius: 4 }}>/var/log/mail.log</code> for bounce reasons</li>
                  <li>SPF / DKIM records for senselifting.com</li>
                  <li>Consider switching to SMTP (PHPMailer + SMTP relay) for reliability</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Edit member drawer ──────────────────────────────────────────── */}
      {editUser && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => setEditUser(null)}>
          <div style={{ background: O.bg2, borderRadius: '24px 24px 0 0', padding: '28px 20px 44px', width: '100%', maxWidth: 480, borderTop: `2px solid ${O.gold}`, boxShadow: '0 -12px 60px rgba(0,0,0,0.7)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ width: 36, height: 4, background: O.border, borderRadius: 2, margin: '0 auto 24px' }} />
            <div style={{ fontSize: 17, fontWeight: 800, color: C.white, marginBottom: 2 }}>{editUser.name || '(no name)'}</div>
            <div style={{ fontSize: 11, color: O.slate, marginBottom: 24, letterSpacing: 0.5 }}>{editUser.email}</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { label: 'LICENSE PLAN', key: 'license', type: 'select', options: LICENSES },
                { label: 'EXPIRES ON', key: 'license_expires_at', type: 'date' },
              ].map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: 10, color: O.slate, letterSpacing: 2, marginBottom: 7 }}>{f.label}</div>
                  {f.type === 'select'
                    ? <select value={editForm[f.key]} onChange={e => setEditForm(v => ({ ...v, [f.key]: e.target.value }))}
                        style={{ width: '100%', background: O.bg4, border: `1px solid ${O.border}`, borderRadius: 10, color: C.white, fontSize: 14, padding: '11px 12px', boxSizing: 'border-box', fontFamily: 'inherit' }}>
                        {f.options.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    : <input type={f.type} value={editForm[f.key] || ''} onChange={e => setEditForm(v => ({ ...v, [f.key]: e.target.value }))}
                        style={{ width: '100%', background: O.bg4, border: `1px solid ${O.border}`, borderRadius: 10, color: C.white, fontSize: 14, padding: '11px 12px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  }
                </div>
              ))}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { label: 'OFFICE ACCESS', key: 'is_admin' },
                  { label: 'LAB ACCESS', key: 'is_researcher' },
                ].map(f => (
                  <div key={f.key}>
                    <div style={{ fontSize: 10, color: O.slate, letterSpacing: 2, marginBottom: 7 }}>{f.label}</div>
                    <select value={editForm[f.key]} onChange={e => setEditForm(v => ({ ...v, [f.key]: e.target.value }))}
                      style={{ width: '100%', background: O.bg4, border: `1px solid ${O.border}`, borderRadius: 10, color: C.white, fontSize: 14, padding: '11px 12px', boxSizing: 'border-box', fontFamily: 'inherit' }}>
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                  </div>
                ))}
              </div>

              <div>
                <div style={{ fontSize: 10, color: O.slate, letterSpacing: 2, marginBottom: 7 }}>ADD CREDITS</div>
                <input type="number" min="0" value={editForm.wallet_credit} onChange={e => setEditForm(v => ({ ...v, wallet_credit: e.target.value }))}
                  style={{ width: '100%', background: O.bg4, border: `1px solid ${O.border}`, borderRadius: 10, color: C.white, fontSize: 14, padding: '11px 12px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginTop: 28 }}>
              <button onClick={() => setEditUser(null)} style={{ background: 'transparent', border: `1px solid ${O.border}`, borderRadius: 12, color: O.slate, fontSize: 13, fontWeight: 700, padding: '14px 0', cursor: 'pointer', letterSpacing: 1 }}>
                CANCEL
              </button>
              <button onClick={saveEdit} disabled={saving} style={{ background: `linear-gradient(135deg, ${O.gold}, #e6c96b)`, border: 'none', borderRadius: 12, color: '#0d1117', fontSize: 13, fontWeight: 800, padding: '14px 0', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, letterSpacing: 1 }}>
                {saving ? 'SAVING…' : 'SAVE CHANGES'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: toast.ok ? '#0f2a1a' : '#2a0f0f', border: `1px solid ${toast.ok ? O.green : O.red}`, borderRadius: 14, padding: '12px 22px', color: toast.ok ? O.green : O.red, fontSize: 13, fontWeight: 700, boxShadow: '0 8px 32px rgba(0,0,0,0.6)', whiteSpace: 'nowrap', letterSpacing: 0.5 }}>
          {toast.ok ? '✓' : '✕'} {toast.msg}
        </div>
      )}
    </div>
  );
}
